import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AssetGroupDto } from '@assetaccountant/api/models/asset-group.dto';
import { AssetTransactionDto } from '@assetaccountant/api/models/asset-transaction.dto';
import { AssetTransactionsWithTotalsDto } from '@assetaccountant/api/models/asset-transactions-with-totals.dto';
import { AssetRegimeResponse } from '@assetaccountant/api/models/assets/asset-regime.response';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { CalendarV2Api } from '@assetaccountant/api/services/calendar-v2.api';
import {
  AssetTransactionAdjusted,
  augmentTransactions,
  getTransactionDescription,
  getTransactionSource,
} from '@assetaccountant/assets/models/asset-transaction.utils';
import { Register, toUniversalDateString } from '@assetaccountant/core';
import {
  ExportButtonComponent,
  GridExportParams,
} from '@assetaccountant/core/components/export-button/export-button.component';
import { GridRowClassRule } from '@assetaccountant/core/components/grid/grid.utils';
import { AaCurrencyPipe } from '@assetaccountant/core/pipes/aa-currency.pipe';
import { AaDatePipe } from '@assetaccountant/core/pipes/aa-date.pipe';
import {
  EXCEL_CURRENCY_CLASS,
  EXCEL_DATE_CLASS,
  finYearValueFormatter,
  getDateFilterParams,
} from '@assetaccountant/core/presentations/cellrenderers';
import { translateTransactionTypeName } from '@assetaccountant/core/presentations/transaction-type-translate';
import { PeriodData, PeriodParams } from '@assetaccountant/core/resolvers/period.resolver';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { slug } from '@assetaccountant/core/utils/slug';
import moment from 'moment';
import { map } from 'rxjs/operators';
import { ALL_TYPES_BUT_REVERSALS } from '../../../models/asset-transaction-api-model';

@Component({
  selector: 'app-view-asset-transactions',
  templateUrl: './view-asset-transactions.component.html',
  styleUrls: ['./view-asset-transactions.component.scss'],
  standalone: false,
})
export class ViewAssetTransactionsComponent implements OnChanges, OnDestroy {
  readonly EXCEL_CURRENCY_CLASS = EXCEL_CURRENCY_CLASS;
  readonly EXCEL_DATE_CLASS = EXCEL_DATE_CLASS;
  readonly RegisterPermission = RegisterPermissionType;

  readonly dateFilterParams = getDateFilterParams();
  readonly fyKeyCreator = finYearValueFormatter(this.locale);

  @Input() register: Register;
  @Input() regime: AssetRegimeResponse;

  @Input() assetName: string;

  @Input() totals?: AssetTransactionsWithTotalsDto;
  @Input() transactions?: AssetTransactionDto[];

  @Input() showPoolAsset = false;

  @Input() assetGroups?: AssetGroupDto[];

  @Input() exportButton?: ExportButtonComponent<AssetTransactionAdjusted>;

  @Output() getMoreTransactions = new EventEmitter<string>();
  @Output() limitWidth = new EventEmitter<boolean>(true);
  @Output() reassess = new EventEmitter<void>();

  showTaxableDepreciation = false;
  get wdvName() {
    if (this.register) {
      return this.register.regimes.find(r => r.id === this.regime.regimeId)?.isAccounts
        ? 'Book Value'
        : 'Tax Value';
    }
    return 'Value';
  }

  get areMoreTransactionsAvailable() {
    const latestWdv = this.transactions
      ? this.transactions[this.transactions.length - 1]?.writtenDownValue
      : null;

    return (
      this.transactions &&
      // Check we haven't got down to zero or residual value
      (latestWdv != null ? latestWdv !== 0 && latestWdv !== this.regime.residualValue : true) &&
      // Check we're still getting more transactions
      (this.previousLoadedTransactionsEndDate !==
        this.transactions[this.transactions.length - 1]?.date ||
        this.transactionsLoading)
    );
  }

  private currencyPipe = new AaCurrencyPipe(this.locale, this.route);

  private subscriptions = [
    this.route.data.subscribe(() => {
      // Reset date check when changing regimes
      this.previousLoadedTransactionsEndDate = undefined;
    }),
  ];

  rowClassRules: Record<string, GridRowClassRule<AssetTransactionAdjusted>> = {
    'is-current': data => {
      const period = this.route.snapshot.data.period;
      const date = toUniversalDateString(data.date);
      return !!date && period.startDate <= date && date <= period.endDate;
    },
    'text-body-secondary': data =>
      !ALL_TYPES_BUT_REVERSALS.includes(data.type) || !!data.isPriorToOpeningBalance,
    'fst-italic': data => {
      switch (data.type) {
        case 'AddToPool':
        case 'TransferToPool':
        case 'PoolSale':
        case 'PoolWriteOff':
          return true;
        default:
          return !!data.isPriorToOpeningBalance;
      }
    },
  };

  getRowHeight = (data?: AssetTransactionAdjusted) => {
    if (!data) {
      return 40;
    }

    const description = getTransactionDescription(this.locale, this.route)(data, this.assetGroups);
    const [sourceText, importName] = getTransactionSource(data);

    const nLines = description ? description.split(/\n/g).length : 0;
    // Description lines are 24 pixels
    const descriptionHeight = nLines > 0 ? nLines * 24 : 0;

    // Source is 20 pixels high
    const sourceHeight = sourceText || importName ? 20 : 0;

    // Preferred height is 40
    return Math.max(40, descriptionHeight + sourceHeight);
  };

  private _includeReversals?: boolean;
  get includeReversals() {
    if (this._includeReversals == null) {
      this._includeReversals = !localStorage.getItem('excludeReversals');
    }
    return this._includeReversals;
  }
  set includeReversals(value: boolean) {
    if (value) {
      localStorage.removeItem('excludeReversals');
    } else {
      localStorage.setItem('excludeReversals', 'true');
    }
    this._includeReversals = value;
  }

  allRowData: readonly AssetTransactionAdjusted[];
  unreversedRowData: readonly AssetTransactionAdjusted[];

  get rowData() {
    return this.includeReversals ? this.allRowData : this.unreversedRowData;
  }

  hasReversals = false;
  hideGrouping = false;
  showGrid = true;

  regimeName$ = this.route.data.pipe(map(({ regime }) => regime.name));
  period$ = this.route.data.pipe(
    map(({ period: { startDate, endDate } }) => [startDate, endDate] as [string, string]),
  );
  periodEndDate$ = this.route.data.pipe(map(d => d.period.endDate as string));

  previousLoadedTransactionsEndDate?: string;
  transactionsLoading = true;

  getTransactionDescription = (() => {
    const getDescription = getTransactionDescription(this.locale, this.route);
    return (data: AssetTransactionAdjusted) => getDescription(data, this.assetGroups);
  })();

  getTransactionSource = (data: AssetTransactionAdjusted) => getTransactionSource(data)[0];

  getTransactionDescriptionAndSource = (data: AssetTransactionAdjusted) => {
    const description = this.getTransactionDescription(data);
    const [source, importName] = getTransactionSource(data);
    if (!source) {
      return description;
    }
    const sourceAndName = importName ? `${source} ${importName}` : source;
    if (!description) {
      return sourceAndName;
    }
    return `${description}\n${sourceAndName}`;
  };

  nextIsJsonExport = false;

  get gridExportParams(): GridExportParams<AssetTransactionAdjusted> {
    let isJsonExport: boolean | undefined;

    return {
      fileName: this.downloadableFileName(`${this.assetName} - Transactions`),
      columnFilter: key => {
        switch (key) {
          case 'openingBalanceLabel':
          case 'financialYear':
          case 'descriptionAndSource':
            return false;

          case 'taxableDepreciation':
            return this.showTaxableDepreciation;
          case 'poolAssetId':
            return this.showPoolAsset;
          default:
            return true;
        }
      },
      processCellCallback: ({ colId, data, value }) => {
        if (colId === 'description') {
          let description = getTransactionDescription(this.locale, this.route)(
            data,
            this.assetGroups,
          );

          if (isJsonExport == null) {
            isJsonExport = this.nextIsJsonExport;
            this.nextIsJsonExport = false;
          }

          if (isJsonExport) {
            if (data.unimpairedValue != null) {
              description += `\nUnimpaired Value: ${this.currencyPipe.transform(
                data.unimpairedValue,
              )}`;
            }
            if (data.unrevaluedValue != null) {
              description += `\nUnrevalued Value: ${this.currencyPipe.transform(
                data.unrevaluedValue,
              )}`;
            }
          }

          return description.trim();
        }
        if (colId === 'source') {
          return getTransactionSource(data)[0];
        }
        if (colId === 'poolAssetId') {
          return data.poolAssetName;
        }
        return value;
      },
    };
  }

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private calendarApi: CalendarV2Api,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<PeriodParams, PeriodData>,
    public aaDatePipe: AaDatePipe,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnChanges() {
    const transactions = this.transactions;
    if (transactions && transactions.length > 0) {
      this.showTaxableDepreciation = this.totals?.hasTaxableUse || false;

      this.calendarApi
        .getYears({
          regimeId: this.regime.regimeId,
          startDate: transactions[0].date,
          endDate: transactions[transactions.length - 1].date,
        })
        .subscribe(({ years }) => {
          [this.allRowData, this.unreversedRowData] = augmentTransactions(transactions, {
            years,
            fyKeyCreator: this.fyKeyCreator,
          });

          const hasSingleFinancialYear = this.allRowData.every(
            t => t.financialYear === this.allRowData[0].financialYear,
          );
          const hideGrouping = hasSingleFinancialYear && !this.regime.openingBalanceDate;
          if (hideGrouping !== this.hideGrouping) {
            this.hideGrouping = hideGrouping;
            this.reloadGrid();
          }

          this.hasReversals = this.allRowData.length !== this.unreversedRowData.length;
          this.transactionsLoading = false;
        });
    } else {
      this.allRowData = [];
      this.unreversedRowData = [];
      this.hasReversals = false;
      this.transactionsLoading = false;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onGetMoreTransactions() {
    if (this.transactions) {
      this.transactionsLoading = true;
      this.previousLoadedTransactionsEndDate = this.transactions[this.transactions.length - 1].date;

      this.getMoreTransactions.emit(
        toUniversalDateString(moment(this.previousLoadedTransactionsEndDate).add(10, 'years'), {
          throwOnFailure: true,
        }),
      );
    }
  }

  formatDate = params => {
    return params?.value ? this.aaDatePipe.transform(params.value) : null;
  };

  getTransactionTypeValue = (data: AssetTransactionAdjusted) =>
    translateTransactionTypeName({ eventType: data?.type });

  isGroupOpenByDefault = ({ field, key }: { field: string; key: any }) => {
    return this.isRowGroupOpenByDefault({ field, key });
  };
  isRowGroupOpenByDefault = ({ field, key }: { field: string; key: any }) => {
    if (field === 'openingBalanceLabel' && key) {
      return false;
    }
    return true;
  };

  poolAssetRoute({ poolAssetId, poolAssetName }: AssetTransactionAdjusted) {
    return ['../../../../../periods/-/assets', `${poolAssetId}-${slug(poolAssetName || '')}`];
  }

  /// Supported characters for a file name are letters, numbers, spaces, ()_-
  // Support are , and . however we remove them
  downloadableFileName(input: string): string {
    let result = input;
    if (input !== null && input !== undefined) {
      result = input.replace(/[^a-z0-9\-()_ ]/gi, '');
    }
    return result;
  }

  reloadGrid() {
    this.showGrid = false;
    this.cd.detectChanges();

    this.showGrid = true;
    this.cd.detectChanges();
  }
}
